import {User} from '@hconnect/apiclient'
import {GenericInput, GenericSelect} from '@hm/fundamental'
import React from 'react'
import {Controller, FieldValues} from 'react-hook-form'

import {useTranslation} from '../../../hooks/useTranslation'
import {UserInformationInputSkeleton} from '../Skeleton/UserInformationInputSkeleton'
import {BrandingForCountry, getBrandingNameFromBrandingId} from '../utils/utils'

import {UserInformationProps} from './types'

type UserBrandingInputProps<T extends FieldValues> = UserInformationProps<T> & {
  brandings: BrandingForCountry[]
  user?: User | null
}

export const UserBrandingInput = <T extends FieldValues>({
  readonlyMode,
  formMethods,
  user,
  brandings,
  isLoading,
  name
}: UserBrandingInputProps<T>) => {
  const {t} = useTranslation()

  const values = formMethods.watch()

  if (isLoading) {
    return <UserInformationInputSkeleton />
  }

  const shouldPreselectBranding = brandings.length > 0 && brandings.length < 2
  const noBrandings = brandings.length === 0

  const handleBrandingsValue = () => {
    if (user?.defaultBranding && readonlyMode) {
      return getBrandingNameFromBrandingId(
        values.availableCountries || [],
        user.defaultBranding,
        user.country || undefined
      )
    } else if (shouldPreselectBranding) {
      return brandings[0].brandingName
    }

    return '-'
  }

  return (
    <>
      {(readonlyMode && user) || noBrandings ? (
        <GenericInput
          label={t('userInformationForm.branding')}
          value={handleBrandingsValue()}
          isReadOnly={true}
        />
      ) : (
        <Controller
          name={name}
          control={formMethods.control}
          render={({field, fieldState}) => (
            <GenericSelect
              {...field}
              setValue={(value) => field.onChange(value)}
              data-cy={`userInformationForm-${field.name}`}
              label={t('userInformationForm.branding')}
              value={(field.value as string) || '-'}
              isReadOnly={readonlyMode}
              isTouched={fieldState.isTouched}
              error={!values.country ? t('userInformationForm.brandingHelperText') : ''}
              values={brandings.map((branding) => ({
                value: branding.brandingId,
                label: branding.brandingName
              }))}
              className="w-full"
            />
          )}
        />
      )}
    </>
  )
}
